/* Inner product carousel (mobile only) + progress bar */
if (!customElements.get('bundle-products')) {
  customElements.define(
    'bundle-products',
    class BundleProducts extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('.mpb__products-viewport');
        this.track = this.querySelector('.mpb__products-track');
        this.progressEl = this.querySelector('.mpb__progress');
        this.swiper = null;
        this._carousel = null;
        this.classes = { swiper: 'swiper', wrapper: 'swiper-wrapper' };

        this.update();
        // Assign onchange (self-replacing) rather than addEventListener, so a
        // section re-render in the theme editor cannot accumulate listeners.
        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.update.bind(this);
      }

      update() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          this.destroySlider();
        }
      }

      initSlider() {
        if (this.swiper || !this.viewport) return;
        this.viewport.classList.add(this.classes.swiper);
        this.track.classList.add(this.classes.wrapper);

        this._carousel = new FoxTheme.Carousel(this.viewport, {
          slidesPerView: 2.1,
          spaceBetween: 8,
          nested: true,
          threshold: 2,
          grabCursor: true,
          pagination: this.progressEl
            ? { el: this.progressEl, type: 'progressbar' }
            : false,
        });
        this._carousel.init();
        this.swiper = this._carousel.slider;
      }

      destroySlider() {
        if (!this.swiper) return;
        this.swiper.destroy(true, true);
        this.swiper = null;
        this._carousel = null;
        this.viewport.classList.remove(this.classes.swiper);
        this.track.classList.remove(this.classes.wrapper);
      }
    }
  );
}

/* One bundle: owns the "add all to cart" */
if (!customElements.get('bundle-slide')) {
  customElements.define(
    'bundle-slide',
    class BundleSlide extends HTMLElement {
      connectedCallback() {
        this.cartDrawer = document.querySelector('cart-drawer');
        this.addButton = this.querySelector('.mpb__add-all');
        this.errorEl = this.querySelector('.product-form__error-message');
        this.addButton && this.addButton.addEventListener('click', this.onAdd.bind(this));
      }

      get lineItems() {
        const items = [];
        this.querySelectorAll('bundle-card').forEach((card) => {
          const input = card.querySelector('input[name="id"]');
          if (!input || input.disabled || !input.value) return;
          items.push({ id: input.value, quantity: 1 });
        });
        return items;
      }

      onAdd(event) {
        event.preventDefault();
        const items = this.lineItems;
        if (!items.length) return;
        this.showError();

        if (
          document.body.classList.contains('cart-template') ||
          FoxTheme.settings.cartType !== 'drawer'
        ) {
          FoxTheme.utils.postLink(FoxTheme.routes.cart_add_url, { parameters: { items } });
          return;
        }

        this.toggleLoading(true);
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
        );
        const body = JSON.stringify({
          items,
          sections,
          section_url: window.location.pathname,
        });

        fetch(FoxTheme.routes.cart_add_url, { ...FoxTheme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              this.showError(parsedState.description);
              return;
            }
            const cartJson = await (
              await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig() })
            ).json();
            cartJson.sections = parsedState.sections;
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
            document.dispatchEvent(
              new CustomEvent('product-ajax:added', { detail: { product: parsedState } })
            );
            this.cartDrawer && this.cartDrawer.show(this.addButton);
          })
          .catch((e) => console.error(e))
          .finally(() => this.toggleLoading(false));
      }

      showError(message = false) {
        if (!this.errorEl) {
          message && alert(message);
          return;
        }
        this.errorEl.hidden = !message;
        if (message) this.errorEl.textContent = message;
      }

      toggleLoading(state) {
        this.addButton.classList.toggle('btn--loading', state);
        if (state) {
          this.addButton.setAttribute('disabled', 'true');
        } else {
          this.addButton.removeAttribute('disabled');
        }
      }
    }
  );
}

/* Outer slider of bundle-slides + arrow routing */
if (!customElements.get('multiple-product-bundles')) {
  customElements.define(
    'multiple-product-bundles',
    class MultipleProductBundles extends HTMLElement {
      connectedCallback() {
        this.enableSlider = this.dataset.enableSlider === 'true';
        this.slideCount = parseInt(this.dataset.slideCount, 10) || 1;
        this.carousel = null;

        this.bindArrows();
        this.update();
        // Assign onchange (self-replacing) rather than addEventListener, so a
        // section re-render in the theme editor cannot accumulate listeners.
        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.update.bind(this);
      }

      get outer() {
        return this.carousel ? this.carousel.slider : null;
      }

      update() {
        if (this.enableSlider) this.initSlider();
        this.updateArrowVisibility();
      }

      initSlider() {
        if (this.carousel) return;
        this.carousel = new FoxTheme.Carousel(this, {
          slidesPerView: 1,
          spaceBetween: 40,
          threshold: 2,
          autoHeight: false,
          on: {
            init: (sw) => this.reflectEdges(sw),
            slideChange: (sw) => this.reflectEdges(sw),
          },
        });
        this.carousel.init();
      }

      // Arrows are routed manually (not Swiper `navigation`), so reflect the
      // begin/end disabled affordance ourselves.
      reflectEdges(sw) {
        this.querySelectorAll('.mpb__arrow--prev').forEach((b) =>
          b.classList.toggle('swiper-button-disabled', sw.isBeginning)
        );
        this.querySelectorAll('.mpb__arrow--next').forEach((b) =>
          b.classList.toggle('swiper-button-disabled', sw.isEnd)
        );
      }

      bindArrows() {
        this.querySelectorAll('.mpb__slide').forEach((slide) => {
          const prev = slide.querySelector('.mpb__arrow--prev');
          const next = slide.querySelector('.mpb__arrow--next');
          prev && prev.addEventListener('click', () => this.onArrow('prev', slide));
          next && next.addEventListener('click', () => this.onArrow('next', slide));
        });
      }

      onArrow(direction, slide) {
        // Multiple bundles: arrows move between slides.
        if (this.enableSlider && this.outer) {
          direction === 'next' ? this.outer.slideNext() : this.outer.slidePrev();
          return;
        }
        // Single bundle: on mobile the arrows drive the inner product carousel.
        const products = slide.querySelector('bundle-products');
        const inner = products && products.swiper;
        if (inner) {
          direction === 'next' ? inner.slideNext() : inner.slidePrev();
        }
      }

      updateArrowVisibility() {
        // Nothing to navigate on desktop when there is a single, fully-visible bundle.
        const hide = !this.enableSlider && !FoxTheme.config.mqlMobile;
        this.querySelectorAll('.mpb__nav').forEach((nav) => {
          nav.classList.toggle('mpb__nav--hidden', hide);
        });
      }
    }
  );
}
